import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from 'react';

/**
 * Politeness levels for screen reader announcements
 * - 'polite': Waits for the user to finish their current task before announcing
 * - 'assertive': Interrupts the user immediately (use sparingly)
 * - 'off': No announcement
 */
type AriaLive = 'polite' | 'assertive' | 'off';

interface Announcement {
  message: string;
  politeness: AriaLive;
  id: number;
}

interface LiveAnnouncerContextType {
  announce: (message: string, politeness?: AriaLive) => void;
  announceSensorUpdate: (
    sensorName: string,
    value: number | string,
    unit: string,
    status?: 'good' | 'warning' | 'error' | 'offline',
  ) => void;
  announceAlert: (
    type: 'info' | 'warning' | 'error' | 'success',
    title: string,
    message?: string,
  ) => void;
  announceStatusChange: (component: string, status: string) => void;
  clearAnnouncements: () => void;
}

const LiveAnnouncerContext = createContext<LiveAnnouncerContextType | null>(null);

interface LiveAnnouncerProviderProps {
  children: ReactNode;
  debounceTime?: number;
  maxQueue?: number;
}

export const LiveAnnouncerProvider: React.FC<LiveAnnouncerProviderProps> = ({
  children,
  debounceTime = 1000,
  maxQueue = 5,
}) => {
  const [politeAnnouncement, setPoliteAnnouncement] = useState('');
  const [assertiveAnnouncement, setAssertiveAnnouncement] = useState('');
  const announcementIdRef = useRef(0);
  const queueRef = useRef<Announcement[]>([]);
  const lastAnnouncementTimeRef = useRef<Record<string, number>>({});
  const processingRef = useRef(false);

  // Process the announcement queue
  const processQueue = useCallback(() => {
    if (processingRef.current || queueRef.current.length === 0) return;

    processingRef.current = true;
    const announcement = queueRef.current.shift();

    if (announcement) {
      if (announcement.politeness === 'assertive') {
        setAssertiveAnnouncement(announcement.message);
      } else {
        setPoliteAnnouncement(announcement.message);
      }

      // Clear after a short delay to allow screen reader to pick it up
      setTimeout(() => {
        if (announcement.politeness === 'assertive') {
          setAssertiveAnnouncement('');
        } else {
          setPoliteAnnouncement('');
        }
        processingRef.current = false;

        // Process next item in queue
        if (queueRef.current.length > 0) {
          setTimeout(processQueue, 100);
        }
      }, 500);
    } else {
      processingRef.current = false;
    }
  }, []);

  // Main announce function
  const announce = useCallback(
    (message: string, politeness: AriaLive = 'polite') => {
      if (!message || politeness === 'off') return;

      // Debounce similar messages
      const messageKey = message.substring(0, 50);
      const now = Date.now();
      const lastTime = lastAnnouncementTimeRef.current[messageKey] || 0;

      if (now - lastTime < debounceTime) {
        return; // Skip if same message was announced recently
      }

      lastAnnouncementTimeRef.current[messageKey] = now;

      // Add to queue
      const announcement: Announcement = {
        message,
        politeness,
        id: ++announcementIdRef.current,
      };

      // Limit queue size
      if (queueRef.current.length >= maxQueue) {
        queueRef.current.shift(); // Remove oldest
      }

      queueRef.current.push(announcement);
      processQueue();
    },
    [debounceTime, maxQueue, processQueue],
  );

  // Specialized announcement for sensor updates
  const announceSensorUpdate = useCallback(
    (
      sensorName: string,
      value: number | string,
      unit: string,
      status?: 'good' | 'warning' | 'error' | 'offline',
    ) => {
      let statusText = '';
      let politeness: AriaLive = 'polite';

      if (status) {
        switch (status) {
          case 'good':
            statusText = ', stav v pořádku';
            break;
          case 'warning':
            statusText = ', varování';
            politeness = 'assertive';
            break;
          case 'error':
            statusText = ', chyba';
            politeness = 'assertive';
            break;
          case 'offline':
            statusText = ', offline';
            break;
        }
      }

      const message = `${sensorName}: ${value} ${unit}${statusText}`;
      announce(message, politeness);
    },
    [announce],
  );

  // Specialized announcement for alerts
  const announceAlert = useCallback(
    (type: 'info' | 'warning' | 'error' | 'success', title: string, message?: string) => {
      let prefix = '';
      let politeness: AriaLive = 'polite';

      switch (type) {
        case 'info':
          prefix = 'Informace: ';
          break;
        case 'warning':
          prefix = 'Varování: ';
          politeness = 'assertive';
          break;
        case 'error':
          prefix = 'Chyba: ';
          politeness = 'assertive';
          break;
        case 'success':
          prefix = 'Úspěch: ';
          break;
      }

      const fullMessage = message ? `${prefix}${title}. ${message}` : `${prefix}${title}`;
      announce(fullMessage, politeness);
    },
    [announce],
  );

  // Specialized announcement for status changes
  const announceStatusChange = useCallback(
    (component: string, status: string) => {
      const message = `${component} změnil stav na ${status}`;
      announce(message, 'polite');
    },
    [announce],
  );

  // Clear all announcements
  const clearAnnouncements = useCallback(() => {
    queueRef.current = [];
    setPoliteAnnouncement('');
    setAssertiveAnnouncement('');
    processingRef.current = false;
  }, []);

  // Cleanup old debounce entries periodically
  useEffect(() => {
    const cleanup = setInterval(() => {
      const now = Date.now();
      const entries = Object.entries(lastAnnouncementTimeRef.current);
      for (const [key, time] of entries) {
        if (now - time > 60000) {
          // Remove entries older than 1 minute
          delete lastAnnouncementTimeRef.current[key];
        }
      }
    }, 30000);

    return () => clearInterval(cleanup);
  }, []);

  const contextValue: LiveAnnouncerContextType = {
    announce,
    announceSensorUpdate,
    announceAlert,
    announceStatusChange,
    clearAnnouncements,
  };

  return (
    <LiveAnnouncerContext.Provider value={contextValue}>
      {children}

      {/* Visually hidden live regions for screen readers */}
      <div
        aria-live="polite"
        aria-atomic="true"
        role="status"
        className="sr-only" // Tailwind utility for visually hidden
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: '0',
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: '0',
        }}
      >
        {politeAnnouncement}
      </div>

      <div
        aria-live="assertive"
        aria-atomic="true"
        role="alert"
        className="sr-only"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: '0',
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: '0',
        }}
      >
        {assertiveAnnouncement}
      </div>
    </LiveAnnouncerContext.Provider>
  );
};

export const useLiveAnnouncer = (): LiveAnnouncerContextType => {
  const context = useContext(LiveAnnouncerContext);

  if (!context) {
    throw new Error('useLiveAnnouncer must be used within a LiveAnnouncerProvider');
  }

  return context;
};

export const useSensorAnnouncements = (
  sensors: Array<{
    id: string;
    name: string;
    value: number;
    unit: string;
    status: 'good' | 'warning' | 'error' | 'offline';
  }>,
  options?: {
    threshold?: number;
    announceStatusChanges?: boolean;
  },
) => {
  const { announceSensorUpdate, announceStatusChange } = useLiveAnnouncer();
  const previousSensorsRef = useRef<Map<string, { value: number; status: string }>>(new Map());
  const { threshold = 0.1, announceStatusChanges = true } = options || {};

  useEffect(() => {
    for (const sensor of sensors) {
      const previous = previousSensorsRef.current.get(sensor.id);

      if (previous) {
        // Check for significant value change
        const valueDiff = Math.abs(sensor.value - previous.value);
        if (valueDiff >= threshold) {
          announceSensorUpdate(sensor.name, sensor.value, sensor.unit, sensor.status);
        }
        // Check for status change
        else if (announceStatusChanges && sensor.status !== previous.status) {
          announceStatusChange(sensor.name, sensor.status);
        }
      }

      // Update previous values
      previousSensorsRef.current.set(sensor.id, {
        value: sensor.value,
        status: sensor.status,
      });
    }
  }, [sensors, threshold, announceStatusChanges, announceSensorUpdate, announceStatusChange]);
};

export default LiveAnnouncerProvider;
